// src/pages/AccountDetails.jsx
import React, { useState } from 'react';
import { WebLayout } from 'components/Layout';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Mail, MapPin, Lock, X, Check, Camera } from 'react-feather';
import { useAuth } from 'lib/auth';
import { useEditAccountDetails } from '../api/editAccountDetails';
import { useEditAvatar } from '../api/editAvatar';

export const AccountDetails = () => {
  const { user, refetchUser } = useAuth();
  const [editingField, setEditingField] = useState(null);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phoneNumber: user?.phoneNumber || '',
    email: user?.email || '',
  });

  const editAccountMutation = useEditAccountDetails({
    onSuccess: () => refetchUser?.(),
  });

  const editAvatarMutation = useEditAvatar({
    onSuccess: () => refetchUser?.(),
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFieldSubmit = async (field) => {
    await editAccountMutation.mutateAsync({ [field]: formData[field] });
    setEditingField(null);
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const avatarData = new FormData();
    avatarData.append('avatar', file);
    await editAvatarMutation.mutateAsync(avatarData);
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <WebLayout>
      <section className="bg-[#FFF6F2] py-16 px-6 sm:px-12 lg:px-24">
        {/* === Page Header === */}
        <motion.div
          className="text-center mb-12"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
        >
          <h1 className="text-3xl sm:text-4xl font-semibold text-[#AC3F1E] mb-3">My Account</h1>
          <p className="text-gray-700 text-sm sm:text-base">
            Manage your personal information, avatar, and account settings.
          </p>
        </motion.div>

        {/* === Profile Summary === */}
        <motion.div
          className="max-w-4xl mx-auto bg-white shadow-md rounded-lg p-8 mb-12 flex flex-col sm:flex-row items-center gap-6 relative"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
        >
          {/* Avatar */}
          <div className="relative group">
            {user?.avatar ? (
              <img
                src={user.avatar}
                alt={user.name}
                className="w-24 h-24 rounded-full object-cover shadow-md border border-gray-200"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-[#AC3F1E]/10 flex items-center justify-center text-[#AC3F1E] text-4xl font-semibold">
                {user?.name
                  ? user.name
                      .split(' ')
                      .map((n) => n[0])
                      .join('')
                      .slice(0, 2)
                  : 'U'}
              </div>
            )}
            {/* Edit Avatar Button */}
            <label className="absolute bottom-0 right-0 bg-[#AC3F1E] p-2 rounded-full cursor-pointer hover:opacity-90 transition shadow-md">
              <Camera size={16} className="text-white" />
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarChange}
              />
            </label>
          </div>

          <div className="text-center sm:text-left">
            <h2 className="text-xl font-semibold text-gray-800 mb-1">{user?.name}</h2>
            <p className="text-sm text-gray-600 flex items-center justify-center sm:justify-start gap-2">
              <Mail size={16} /> {user?.email}
            </p>
          </div>
        </motion.div>

        {/* === Account Form Sections === */}
        <motion.div
          className="max-w-4xl mx-auto space-y-12"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
        >
          {/* --- Personal Info --- */}
          <div className="bg-white shadow-md rounded-lg p-8">
            <h3 className="text-lg font-semibold text-[#AC3F1E] mb-6 flex items-center gap-2">
              <User size={20} /> Personal Information
            </h3>
            <form className="grid grid-cols-1 sm:grid-cols-2 gap-6 relative">
              {['name', 'phoneNumber', 'email'].map((field) => (
                <div key={field} className="relative">
                  <label className="block text-sm text-gray-700 mb-1 capitalize">
                    {field.replace(/([A-Z])/g, ' $1')}
                  </label>
                  <input
                    name={field}
                    type={field === 'email' ? 'email' : 'text'}
                    value={formData[field]}
                    onFocus={() => setEditingField(field)}
                    onChange={handleInputChange}
                    onKeyDown={(e) => e.key === 'Enter' && handleFieldSubmit(field)}
                    className={`w-full border border-gray-300 px-4 py-2 rounded-md focus:outline-none focus:border-[#AC3F1E] ${
                      editingField === field ? 'ring-2 ring-[#AC3F1E]/50' : ''
                    }`}
                  />

                  {/* Floating Buttons on Right */}
                  <AnimatePresence>
                    {editingField === field && (
                      <motion.div
                        className="absolute -bottom-10 right-0 flex gap-2"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.3 }}
                      >
                        <button
                          type="button"
                          onClick={() => setEditingField(null)}
                          className="bg-gray-200 hover:bg-gray-300 p-2 rounded-full shadow-sm"
                          title="Cancel"
                        >
                          <X size={16} className="text-gray-600" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleFieldSubmit(field)}
                          className="bg-[#AC3F1E] hover:bg-[#922f12] p-2 rounded-full shadow-sm"
                          title="Save"
                        >
                          <Check size={16} className="text-white" />
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </form>
          </div>
        </motion.div>
      </section>
    </WebLayout>
  );
};
